import React, {useState} from 'react';
import graphql from "babel-plugin-relay/macro";
import {useMutation, usePreloadedQuery} from "react-relay";
import {useLoaderData} from "react-router-dom";
import {useUser} from "../../hooks/useUser";
import DetailsPageHeader from "../../components/common/headers/DetailsPageHeader";
import PractitionerLocationVersions from "./versions/PractitionerLocationVersions";
import {Card, Col, Form, Row, Tab} from "react-bootstrap";
import PractitionerLocationTaxonomies from "./taxonomies/PractitionerLocationTaxonomies";
import PractitionerLocationPracticePeriods from "./practice-periods/PractitionerLocationPracticePeriods";
import PractitionerLocationLineOfService from "./linesOfService/PractitionerLocationLineOfService";
import DeletePanel from "../../components/common/repeated-page-panels/DeletePanel";
import MultiModeDetailsPageHeader from "../../components/common/headers/MultiModeDetailsPageHeader";
import InfoPanel from "../../components/common/repeated-page-panels/InfoPanel";
import HorizontalFormInput from "../../components/common/horizontal-form-inputs/HorizontalFormInput";
import IconButton from "../../components/common/IconButton";
import {FormProvider, useForm} from "react-hook-form";
import {toast} from "react-toastify";
import PractitionerLocationInfo from "./PractitionerLocationInfo";
import PractitionerLocationCmsSpecialties from "./cmsSpecialties/PractitionerLocationCmsSpecialties";
import PractitionerLocationTitle from "./PractitionerLocationTitle";

const PractitionerLocationDetails = () => {
    const loader = useLoaderData();
    const data = usePreloadedQuery(graphql`
        query PractitionerLocationDetailsQuery($id: ID!) {
            node(id:$id) {
                id
                ... on PractitionerLocation {
                    practitioner {
                        id
                        applicableVersion {
                            displayName
                        }
                        legalContract {
                            linesOfService {
                                ...ContractLinesOfServiceFragments_LinesOfService
                            }
                        }
                        identifiers {
                            identifierType {
                                code
                            }
                            value
                        }
                    }
                    location {
                        addressLine1
                        addressLine2
                        city
                        state
                        postalCode
                        applicableVersion {
                            name
                        }
                    }
                    legalContractLineOfService {
                        id
                        lineOfService {
                            id
                            name
                        }
                    }
                    applicableVersion {
                        id
                        mondayHours
                        tuesdayHours
                        wednesdayHours
                        thursdayHours
                        fridayHours
                        saturdayHours
                        sundayHours
                        acceptingNewPatients
                        facilityWheelchairAccessible
                        requiresReferral
                        appointmentWaitTime
                        effectiveFrom
                        effectiveTo
                        showInDirectory
                        providesHomeVisits
                        providesOfficeVisits
                        providesTelehealth
                    }
                    linesOfService {
                        ...PractitionerLocationLineOfService
                    }
                    versions {
                        ...PractitionerLocationVersions_Versions
                    }
                    cmsSpecialties { 
                        ...PractitionerLocationCmsSpecialties_CmsSpecialties
                    }
                    taxonomies {
                        ...PractitionerLocationTaxonomies_Taxonomy
                    }
                    practicePeriods {
                        ...PractitionerLocationPracticePeriods_PracticePeriod
                    }
                }
            }
            allTaxonomies {
                ...Fragments_AllTaxonomiesDropdown
            }
             allCmsSpecialties {
                ...Fragments_AllCmsSpecialtiesDropdown
            }
        }
    `, loader.practitionerLocationDetailsQuery);
    const deleteMutation = graphql`
    mutation PractitionerLocationDetails_DeleteMutation($id:ID!) {
        deleteNode(input:{id: $id}) {
                deletedNodeId @deleteRecord
            owner {
                ... on Practitioner
                {
                    locations {
                        ...PractitionerLocations_Locations
                    }
                }
                ... on Location {
                    practitioners {
                        ...LocationPractitioners_Practitioners
                    }
                }
            }
        }
    }`
    const {hasPermission} = useUser();
    return (
        <Tab.Container defaultActiveKey={"current"}>
            <MultiModeDetailsPageHeader iconText={"Practitioner Location"} 
                               iconType={"user-md"}
                                        title={<PractitionerLocationTitle data={data?.node} />}
            />
            <Tab.Content>
                <Tab.Pane eventKey={"current"}>
                    <Row>
                        <Col>
                            <PractitionerLocationInfo data={data} displayMode={"current"}/>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <PractitionerLocationTaxonomies practitionerLocationId={data.node.id}
                                                            taxonomies={data.node.taxonomies}
                                                            allTaxonomies={data.allTaxonomies}
                                                            displayMode={"current"}
                            />
                        </Col>
                    </Row>
                </Tab.Pane>
                <Tab.Pane eventKey={"historical"}>
                    <Row>
                        <Col>
                            <PractitionerLocationVersions practitionerLocationId={data.node.id}
                                                          versions={data.node.versions}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <PractitionerLocationTaxonomies practitionerLocationId={data.node.id}
                                                            taxonomies={data.node.taxonomies}
                                                            allTaxonomies={data.allTaxonomies}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <PractitionerLocationCmsSpecialties practitionerLocationId={data.node.id}
                                                                cmsSpecialties={data.node.cmsSpecialties}
                                                                allCmsSpecialties={data.allCmsSpecialties}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <PractitionerLocationPracticePeriods practitionerLocationId={data.node.id}
                                                                 practicePeriods={data.node.practicePeriods}
                            />
                        </Col>
                    </Row>
                    <Row>
                        {hasPermission("PractitionerLocation.Delete") && <DeletePanel mutation={deleteMutation}
                                                                                      redirectUrl={"/practitioners/" + data.node.practitioner.id}
                                                                                      thingToDelete={"Practitioner Location"}
                                                                                      id={data.node.id}>
                            To delete a Practitioner Location, you must have only 1 version and no other related data.
                        </DeletePanel>}
                    </Row>
                </Tab.Pane>
            </Tab.Content>
            
        </Tab.Container>
    )
}
export default PractitionerLocationDetails